import type { StationeryTemplate } from './DesignsGallery.types';
import type { ColorwayColorHit } from './useColorwayColorHits';
import { colorwayOrderForSelection, EMPTY_COLORWAY_ORDER, templateIdsByColorOption } from './useColorwayColorHits';
import { useActivePrintFiltersVar } from './utils/activePrintFiltersCache';
import { COLOR_FACET } from './components/Filters/facetOptions';
import { useMemo } from 'react';

export type TagFilteredTemplates = Readonly<{
  templates: ReadonlyArray<StationeryTemplate>;
  /**
   * `themeId` → colorway ids matching the selected colors, best-first. Empty
   * unless a color filter is active; consumed by tiles to order swatches and
   * preselect the matching colorway, so a card filtered as "blue" renders blue.
   */
  colorwayOrder: ReadonlyMap<string, ReadonlyArray<string>>;
  /**
   * Whether the user has selected any color. Callers use this to stand down
   * behavior that would contradict the selection — notably the display-order
   * flag's repeated tiles, which each pre-select a colorway of their own.
   */
  isColorFilterActive: boolean;
}>;

export const useTagFilteredTemplates = (templates: ReadonlyArray<StationeryTemplate>, colorwayColorHits: ReadonlyArray<ColorwayColorHit>): TagFilteredTemplates => {
  const activePrintFilters = useActivePrintFiltersVar();

  const selectedColorOptions = useMemo(() => new Set([...activePrintFilters].filter(option => option.startsWith(`${COLOR_FACET}:`))), [activePrintFilters]);

  // A template's `tags` describe a single palette, so a color filter alone would
  // miss the card whose *other* colorways are that color. Union in the templates
  // matched per colorway; the facet counts do the same union (`useFilterFacets`).
  const colorwayMatchedTemplateIds = useMemo(() => {
    if (!selectedColorOptions.size) return new Set<string>();
    const idsByOption = templateIdsByColorOption(colorwayColorHits);
    const ids = new Set<string>();
    for (const option of selectedColorOptions) for (const id of idsByOption.get(option) ?? []) ids.add(id);
    return ids;
  }, [colorwayColorHits, selectedColorOptions]);

  const filteredTemplates = useMemo(() => {
    if (!activePrintFilters.size) return templates;

    return templates.filter(template => {
      return template.mappedTags.some(tagCompositeCode => activePrintFilters.has(tagCompositeCode)) || colorwayMatchedTemplateIds.has(template.id);
    });
  }, [templates, activePrintFilters, colorwayMatchedTemplateIds]);

  const colorwayOrder = useMemo(
    () => (selectedColorOptions.size ? colorwayOrderForSelection(colorwayColorHits, selectedColorOptions) : EMPTY_COLORWAY_ORDER),
    [colorwayColorHits, selectedColorOptions],
  );

  return { templates: filteredTemplates, colorwayOrder, isColorFilterActive: selectedColorOptions.size > 0 };
};
